import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/Card';
import { SifEmployeeIssue, sifEmployeeIssueFixPath } from '../../../../../models/payroll';

interface KsaIssueCardsProps {
  warningIssues: SifEmployeeIssue[];
  blockingIssues: SifEmployeeIssue[];
}

export const KsaIssueCards: React.FC<KsaIssueCardsProps> = ({ warningIssues, blockingIssues }) => {
  const { t } = useTranslation();
  return (
    <>
      {/* Coverage warnings — non-blocking (e.g. CCHI gaps); the wage file still generates. */}
      {warningIssues.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold">
                {t('payroll.complianceCenter.wps.coverageWarningsHeader', 'Coverage Warnings')}
              </h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {t('payroll.complianceCenter.wps.wontBlock', "Won't block filing")}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t('payroll.complianceCenter.wps.coverageWarningsSubtitle', 'The wage file will still generate. Resolve these before the bank deadline.')}
            </p>
            <ul className="divide-y divide-border">
              {warningIssues.map((issue, i) => (
                <li key={(issue.userId + issue.issue) || i} className="py-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-700">{issue.employeeName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {/cchiExpired/i.test(issue.issue)
                          ? t('payroll.complianceCenter.wps.cchiExpired', 'CCHI health-insurance policy has expired.')
                          : t('payroll.complianceCenter.wps.cchiMissing', 'No in-force CCHI health-insurance policy (required for non-Saudis).')}
                      </p>
                      <Link to={`/profile/${encodeURIComponent(issue.userId)}/payroll/benefits#cchi`} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        {t('payroll.complianceCenter.wps.fixInCchi', 'Fix in CCHI')}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Per-employee blocking issues — warnings (e.g. CCHI) live in the Coverage Warnings card above. */}
      {blockingIssues.length > 0 && (
        <div id="employee-issues" className="scroll-mt-24">
          <Card className="border-red-200">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-1 flex items-center gap-2 text-red-700">
                <XCircle className="w-4 h-4 text-red-500" />
                {t('payroll.complianceCenter.wps.employeeIssuesHeader', 'Employee Pre-Validation Issues')} ({blockingIssues.length})
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {t('payroll.complianceCenter.wps.employeeIssuesSubtitle', "Employees missing the data the SIF requires. Use “Fix this” to jump to the employee's profile and clear each one.")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="text-start py-2 font-medium">{t('common.employee', 'Employee')}</th>
                      <th className="text-start py-2 font-medium">{t('common.issue', 'Issue')}</th>
                      <th className="text-start py-2 font-medium">{t('common.field', 'Field')}</th>
                      <th className="text-end py-2 font-medium">{t('common.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockingIssues.map((issue, i) => {
                      const fixTo = sifEmployeeIssueFixPath(issue.issue, issue.userId);
                      return (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-2 font-medium">
                            {fixTo ? (
                              <Link to={fixTo} className="text-primary hover:underline">
                                {issue.employeeName}
                              </Link>
                            ) : (
                              issue.employeeName
                            )}
                          </td>
                          <td className="py-2 text-red-600">
                            {t(`payroll.complianceCenter.ksaWps.issue.${issue.issue}`, issue.issue)}
                          </td>
                          <td className="py-2 font-mono text-xs">{issue.field}</td>
                          <td className="py-2 text-end">
                            {fixTo && (
                              <Link
                                to={fixTo}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                              >
                                {t('payroll.complianceCenter.wps.fixThis', 'Fix this')}
                                <ArrowRight className="w-3 h-3" />
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
